import { useEffect, useRef, useState } from "react";
import { useGetPresignedUrlMutation, useUploadFileToPresignedUrlMutation } from "../../pages/Common/commonService";
import { useDispatch } from "react-redux";
import { setAlertFunc } from "../Toaster/ToasterSlice";
import { useTranslation } from "react-i18next";
import compressImage, { COMPRESSED_LIMIT_MESSAGE, exceedsCompressedLimit } from "../../utils/compressImage";

/**
 * Extras used by UploadImageV2 only. The commissioning lane calls this hook with
 * the five positional arguments and no options, so every default below keeps
 * that lane's behaviour exactly as it was.
 */
type UploadOptions = {
    reportType?: string
    maxFiles?: number
    /** Per-file size cap in bytes; 0 disables the check. */
    maxBytes?: number
    /** MIME types accepted; defaults to the image-only set. */
    allowedTypes?: string[]
    /**
     * Wait for the S3 PUTs to finish before showing the file and reporting
     * `isUploading`. Off by default: commissioning fires them and returns
     * immediately, and changing that would alter when its thumbnails appear.
     */
    awaitUpload?: boolean
}

const useUploadImage = (
    fieldName: string,
    setSelectedFiles: any,
    imagesData?: any,
    setDeletedFiles?: any,
    isFetching?: any,
    options: UploadOptions = {}
) => {
    const {
        reportType = "commissioning",
        maxFiles = 3,
        maxBytes = 0,
        allowedTypes = ["image/png", "image/jpeg"],
        awaitUpload = false,
    } = options

    const { t: translate } = useTranslation()

    const dispatch = useDispatch()

    const [getPresignedUrl] = useGetPresignedUrlMutation()
    const [uploadFileToPresignedUrl] = useUploadFileToPresignedUrlMutation()

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [images, setImages] = useState<any[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);

    useEffect(() => {
        if (imagesData && imagesData.length > 0) {
            const existingPreviews = imagesData.map((data: any) => ({
                file: null,
                url: data.url,
                // Carried for the file-chip label; absent for commissioning,
                // which renders by url alone.
                name: data.name,
                type: data.type,
                isExisting: true,
                doc_id: data.doc_id
            }));

            setImages(existingPreviews);
        }
    }, [isFetching]);

    const handleUploadClick = () => {
        if (fileInputRef?.current) {
            fileInputRef?.current.click()
        }
    };


    const handleFilesChange = async (event: any) => {
        const files = Array.from(event?.target?.files) as File[];
        const remainingSlots = maxFiles - images?.length;

        const invalidFiles = files.filter(
            (file) => !allowedTypes.includes(file.type)
        )

        if (invalidFiles.length > 0) {
            const names = allowedTypes
                .map((type) => (type.split("/")[1] ?? type).toUpperCase())
                .join(" or ")
            dispatch(
                setAlertFunc({
                    type: "error",
                    message: `Only ${names} formats are allowed`
                })
            )
            // event.target.value = null;
            return;
        }

        if (remainingSlots <= 0 || files.length > remainingSlots) {
            dispatch(
                setAlertFunc({
                    type: "error",
                    message: `You can upload a maximum of ${maxFiles} images.`
                })
            )
            return;
        }

        event.target.value = null;

        // Any photo size is accepted; it is compressed here and only rejected
        // if it is still over the compressed cap. Other files are not limited.
        setIsCompressing(true)
        const limitedFiles: any = await Promise.all(files.slice(0, remainingSlots).map(compressImage))
        setIsCompressing(false)

        if (limitedFiles.some(exceedsCompressedLimit)) {
            dispatch(
                setAlertFunc({
                    type: "error",
                    message: COMPRESSED_LIMIT_MESSAGE
                })
            )
            return;
        }

        // §2.3 — optional per-group cap; no field sets one at present.
        const oversized = maxBytes > 0 ? limitedFiles.filter((file: File) => file.size > maxBytes) : []
        if (oversized.length > 0) {
            dispatch(
                setAlertFunc({
                    type: "error",
                    message: `Each file must be ${Math.round(maxBytes / (1024 * 1024))} MB or smaller.`
                })
            )
            return;
        }

        const fileNames = limitedFiles.map((item: any) => item.name)

        const payload = {
            report_type: reportType,
            files: {
                [fieldName]: fileNames,
            }
        }

        setIsUploading(true)
        try {
            const response: any = await getPresignedUrl(payload)
            const presignedUrls = response?.data?.data?.[fieldName]

            // A failed presign previously fell through as an empty array, which
            // then crashed reading `.doc_id` off an absent entry. Stop here.
            if (
                response?.data?.meta?.status_code !== 200 ||
                !Array.isArray(presignedUrls) ||
                presignedUrls.length < limitedFiles.length
            ) {
                dispatch(
                    setAlertFunc({
                        type: "error",
                        message: "Could not prepare the upload. Please try again."
                    })
                )
                return;
            }

            // A presign row without an upload_url would PUT to `undefined`,
            // which fetchBaseQuery resolves against the API base — the doc gets
            // created but nothing reaches S3, and the saved image later 404s
            // with NoSuchKey.
            const missingUrl = presignedUrls.some((item: any) => !item?.upload_url)
            if (missingUrl) {
                dispatch(
                    setAlertFunc({
                        type: "error",
                        message: "Upload could not start — the storage link was missing."
                    })
                )
                return;
            }

            const puts = presignedUrls.map((item: any, index: number) =>
                uploadFileToPresignedUrl({ url: item?.upload_url, payload: limitedFiles[index] })
            )

            // Only wait when the caller renders an uploading state; otherwise
            // these stay fire-and-forget, as the commissioning lane expects.
            if (awaitUpload) {
                // `unwrap()` matters: a mutation trigger RESOLVES with { error },
                // so awaiting the triggers alone treated a failed S3 PUT as a
                // success and attached a doc_id for a file that was never stored.
                const results = await Promise.allSettled(puts.map((put: any) => put.unwrap()))
                const failed = results.filter(result => result.status === "rejected").length
                if (failed > 0) {
                    dispatch(
                        setAlertFunc({
                            type: "error",
                            message:
                                failed === limitedFiles.length
                                    ? "Upload failed. Please try again."
                                    : `${failed} of ${limitedFiles.length} files failed to upload. Please try again.`
                        })
                    )
                    return;
                }
            }

            const presignedUrlData = presignedUrls.map((item: any) => ({
                doc_id: item?.doc_id,
                file_url: item?.file_url,
            }))

            await handleUpdateSelectedFiles(limitedFiles, presignedUrlData)
        } catch {
            dispatch(
                setAlertFunc({
                    type: "error",
                    message: "Could not upload the file. Please try again."
                })
            )
        } finally {
            setIsUploading(false)
        }
    };

    const handleUpdateSelectedFiles = async (filesData: File[], presignedUrlData: any) => {
        filesData.forEach((file: any, index: number) => {
            const docId = presignedUrlData[index].doc_id
            const fileURL = presignedUrlData[index].file_url
            file.doc_id = docId
            file.file_url = fileURL
        })

        const newSelectedFiles = filesData.map((file: any) => {
            return {
                doc_id: file.doc_id,
                file_url: file.file_url
            }
        })
        const newImageFiles = filesData.map((file: any) => {
            return {
                ...file,
                // name/size/type are prototype getters on File, so the spread
                // above drops them — copy explicitly for file-chip rendering.
                name: file.name,
                size: file.size,
                type: file.type,
                url: URL.createObjectURL(file),
                isExisting: false,
            }
        })
        setSelectedFiles((prevState: any) => {
            const prevForField = prevState[fieldName] ?? [];
            return {
                ...prevState,
                [fieldName]: [...prevForField, ...newSelectedFiles],
            };
        });
        setImages((prev) => [...prev, ...newImageFiles]);

    }

    const removeImage = (docId: any) => {
        const imageToRemove = images.find(
            (item: any) => item.doc_id === docId
        );

        if (!imageToRemove) return;

        if (!imageToRemove.isExisting && imageToRemove.file) {
            URL.revokeObjectURL(imageToRemove.url);
        }

        setImages((prev) => prev.filter((item: any) => item.doc_id !== docId));

        setDeletedFiles && imageToRemove.isExisting && setDeletedFiles((prevState: any) => [...prevState, imageToRemove?.doc_id]);

        setSelectedFiles((prevState: any) => {
            const current = prevState[fieldName] ?? [];
            const updated = current.filter((item: any) => item.doc_id !== docId);
            return {
                ...prevState,
                [fieldName]: updated,
            };
        });
    };

    return {
        states: {
            fileInputRef,
            images,
            isUploading,
            isCompressing
        },
        functions: {
            handleUploadClick,
            handleFilesChange,
            removeImage,
            translate
        }
    }
}
export default useUploadImage